import React from "react";
import { ChevronDown, ListFilter, Search, X } from "lucide-react";
import {
  HotelServiceProviderType,
  HotelServiceStatus,
  HotelServicesFilters,
  PriceRange,
} from "@/types/hotelService";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import { useHotel } from "@/hooks/useHotel";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { useTeams } from "@/components/pages/staff/hooks/useTeams";
import { useServiceCategories } from "@/components/pages/services/hooks/categories.hooks";
import { usePartnerServiceFilterOptions } from "@/hooks/useHotelServices";
import {
  ALL_STATUES,
  ALL_CATEGORIES,
  ANY_PRICE,
} from "@/contexts/ServiceContext";

// ─── Constants ───────────────────────────────────────────────────────────────

const PRICE_MIN = 0;
const PRICE_MAX = 1000;

const CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const SECTION_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

const OPTION_ROW_CLASS =
  "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5";

const SECTION_PANEL_CLASS =
  "overflow-hidden rounded-md border border-[#E5E5E5] bg-white";

// ─── Draft type ───────────────────────────────────────────────────────────────

type ServiceFilterDraft = {
  statuses: string[];
  categoryId: string;
  tagIds: number[];
  price: string;
  responsibleIds: string[];
  panelSearch: string;
};

function draftFromFilters(
  filters: ServiceFilterPopUpProps["filters"],
): ServiceFilterDraft {
  const statuses: string[] =
    filters.status === ALL_STATUES || !filters.status
      ? [HotelServiceStatus.ACTIVE, HotelServiceStatus.INACTIVE]
      : [filters.status];
  return {
    statuses,
    categoryId: filters.category ?? ALL_CATEGORIES,
    tagIds: filters.partnerTagIds ?? filters.tagIds ?? [],
    price: filters.price ?? ANY_PRICE,
    responsibleIds: filters.responsibleIds ?? [],
    panelSearch: "",
  };
}

function countActiveFilters(
  draft: ServiceFilterDraft,
  isPartnersService: boolean,
): number {
  const statusActive =
    draft.statuses.length < 2 && draft.statuses.length > 0 ? 1 : 0;
  let categoryActive = 0;
  if (isPartnersService) {
    categoryActive = draft.tagIds.length;
  } else if (draft.categoryId !== ALL_CATEGORIES) {
    categoryActive = 1;
  }
  const priceActive = draft.price !== ANY_PRICE ? 1 : 0;
  const responsibleActive = draft.responsibleIds.length;
  return statusActive + categoryActive + priceActive + responsibleActive;
}

function matchSearch(label: string, q: string): boolean {
  return !q.trim() || label.toLowerCase().includes(q.trim().toLowerCase());
}

function priceToSlider(
  price: string,
  min: number,
  max: number,
): [number, number] {
  if (price === ANY_PRICE) return [min, max];
  const [a, b] = price.split(":").map(Number);
  return [a ?? min, b ?? max];
}

function sliderToPrice(
  values: [number, number],
  min: number,
  max: number,
): string {
  if (values[0] === min && values[1] === max) return ANY_PRICE;
  return `${values[0]}:${values[1]}`;
}

function formatPriceRange(price: string, min: number, max: number): string {
  const [a, b] = priceToSlider(price, min, max);
  return `€${a.toFixed(2)}-€${b.toFixed(2)}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]">
      <span className="truncate">{label}</span>
      <Button
        type="button"
        variant="span"
        className="rounded p-0.5 hover:bg-[#DDD6FE]"
        onClick={onRemove}
        aria-label="Remove"
      >
        <X className="size-3.5 shrink-0" />
      </Button>
    </span>
  );
}

function FilterCheckboxRow({
  checked,
  label,
  onCheckedChange,
  children,
}: {
  checked: boolean;
  label?: string;
  onCheckedChange: () => void;
  children?: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        OPTION_ROW_CLASS,
        "cursor-pointer",
        checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={CHECKBOX_CLASS}
      />
      {children ?? (
        <span className="text-sm leading-normal font-normal text-[#171717]">
          {label}
        </span>
      )}
    </label>
  );
}

function ExpandableSection({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="span"
        className={SECTION_TRIGGER_CLASS}
        onClick={onToggle}
      >
        {title}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-[#737373] transition-transform",
            expanded && "rotate-180",
          )}
        />
      </Button>
      {expanded ? children : null}
    </div>
  );
}

function StatusSection({
  draft,
  setDraft,
  expanded,
  onToggle,
}: {
  draft: ServiceFilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<ServiceFilterDraft>>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const toggle = (s: string) =>
    setDraft((prev) => {
      const has = prev.statuses.includes(s);
      const next = has
        ? prev.statuses.filter((x) => x !== s)
        : [...prev.statuses, s];
      return { ...prev, statuses: next };
    });

  return (
    <ExpandableSection title="Status" expanded={expanded} onToggle={onToggle}>
      <div className={cn(SECTION_PANEL_CLASS, "p-1")}>
        <FilterCheckboxRow
          checked={draft.statuses.includes(HotelServiceStatus.ACTIVE)}
          label="Active"
          onCheckedChange={() => toggle(HotelServiceStatus.ACTIVE)}
        />
        <FilterCheckboxRow
          checked={draft.statuses.includes(HotelServiceStatus.INACTIVE)}
          label="Inactive"
          onCheckedChange={() => toggle(HotelServiceStatus.INACTIVE)}
        />
      </div>
    </ExpandableSection>
  );
}

function CategorySection({
  draft,
  setDraft,
  categories,
  multiSelect,
  expanded,
  onToggle,
}: {
  draft: ServiceFilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<ServiceFilterDraft>>;
  categories: { id: string; name: string }[];
  multiSelect: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const visible = categories.filter((c) => matchSearch(c.name, search));
  const toggleCategory = (categoryId: string) => {
    if (!multiSelect) {
      setDraft((prev) => ({
        ...prev,
        categoryId:
          prev.categoryId === categoryId ? ALL_CATEGORIES : categoryId,
      }));
      return;
    }

    const tagId = Number(categoryId);
    if (Number.isNaN(tagId)) return;

    setDraft((prev) => {
      const has = prev.tagIds.includes(tagId);
      return {
        ...prev,
        tagIds: has
          ? prev.tagIds.filter((item) => item !== tagId)
          : [...prev.tagIds, tagId],
      };
    });
  };

  return (
    <ExpandableSection title="Category" expanded={expanded} onToggle={onToggle}>
      <div className={SECTION_PANEL_CLASS}>
        <div className="border-b border-[#E5E5E5] p-2">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 border-[#E5E5E5] text-sm text-[#171717] shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[176px] overflow-y-auto p-1">
          {visible.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-[#737373]">
              No categories found
            </p>
          )}
          {visible.map((cat) => (
            <FilterCheckboxRow
              key={cat.id}
              checked={
                multiSelect
                  ? draft.tagIds.includes(Number(cat.id))
                  : draft.categoryId === cat.id
              }
              label={cat.name}
              onCheckedChange={() => toggleCategory(cat.id)}
            />
          ))}
        </div>
      </div>
    </ExpandableSection>
  );
}

function PriceSection({
  draft,
  setDraft,
  min,
  max,
  expanded,
  onToggle,
}: {
  draft: ServiceFilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<ServiceFilterDraft>>;
  min: number;
  max: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const sliderValue = priceToSlider(draft.price, min, max);

  const handleSliderChange = (values: number[]) => {
    setDraft((prev) => ({
      ...prev,
      price: sliderToPrice(
        [values[0], values[1]] as [number, number],
        min,
        max,
      ),
    }));
  };

  return (
    <ExpandableSection title="Price" expanded={expanded} onToggle={onToggle}>
      <div className={cn(SECTION_PANEL_CLASS, "space-y-3 px-4 py-4")}>
        <Slider
          min={min}
          max={max}
          step={1}
          value={sliderValue}
          onValueChange={handleSliderChange}
          className="w-full"
        />
        <p className="text-right text-sm font-normal text-[#171717]">
          {formatPriceRange(draft.price, min, max)}
        </p>
      </div>
    </ExpandableSection>
  );
}

function getInitials(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return (
    [firstName?.[0], lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    "?"
  );
}

function ResponsibleSection({
  draft,
  setDraft,
  staffOptions,
  teamOptions,
  expanded,
  onToggle,
}: {
  draft: ServiceFilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<ServiceFilterDraft>>;
  staffOptions: { value: string; label: string; initials: string }[];
  teamOptions: { value: string; label: string }[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const allOptions = [
    ...teamOptions.map((t) => ({
      ...t,
      initials: t.label.slice(0, 2).toUpperCase(),
    })),
    ...staffOptions,
  ];
  const visible = allOptions.filter((o) => matchSearch(o.label, search));

  const toggle = (val: string) =>
    setDraft((prev) => {
      const has = prev.responsibleIds.includes(val);
      return {
        ...prev,
        responsibleIds: has
          ? prev.responsibleIds.filter((x) => x !== val)
          : [...prev.responsibleIds, val],
      };
    });

  return (
    <ExpandableSection title="Manager" expanded={expanded} onToggle={onToggle}>
      <div className={SECTION_PANEL_CLASS}>
        <div className="border-b border-[#E5E5E5] p-2">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 border-[#E5E5E5] text-sm text-[#171717] shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[220px] overflow-y-auto p-1">
          {visible.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={draft.responsibleIds.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
            >
              <Avatar className="size-6 shrink-0">
                <AvatarFallback className="bg-[#E5E5E5] text-xs text-[#737373]">
                  {opt.initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm leading-normal font-normal text-[#171717]">
                {opt.label}
              </span>
            </FilterCheckboxRow>
          ))}
        </div>
      </div>
    </ExpandableSection>
  );
}

// ─── Active chips ─────────────────────────────────────────────────────────────

function ActiveChips({
  draft,
  setDraft,
  categories,
  isPartnersService,
  staffAndTeamOptions,
  priceMin,
  priceMax,
}: {
  draft: ServiceFilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<ServiceFilterDraft>>;
  categories: { id: string; name: string }[];
  isPartnersService: boolean;
  staffAndTeamOptions: { value: string; label: string }[];
  priceMin: number;
  priceMax: number;
}) {
  const activeStatus = draft.statuses.length < 2 && draft.statuses.length > 0;
  const activeCategory = isPartnersService
    ? draft.tagIds.length > 0
    : draft.categoryId !== ALL_CATEGORIES;
  const activePrice = draft.price !== ANY_PRICE;
  const hasChips =
    activeStatus ||
    activeCategory ||
    activePrice ||
    draft.responsibleIds.length > 0;

  if (!hasChips) return null;

  const categoryName = categories.find((c) => c.id === draft.categoryId)?.name;
  const categoryById = new Map(
    categories.map((category) => [category.id, category.name]),
  );
  const removePartnerTag = (tagId: number) => {
    setDraft((prev) => ({
      ...prev,
      tagIds: prev.tagIds.filter((item) => item !== tagId),
    }));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {activeStatus &&
        draft.statuses.map((s) => (
          <FilterChip
            key={s}
            label={s === HotelServiceStatus.ACTIVE ? "Active" : "Inactive"}
            onRemove={() =>
              setDraft({
                ...draft,
                statuses: draft.statuses.filter((x) => x !== s),
              })
            }
          />
        ))}
      {!isPartnersService && activeCategory && categoryName && (
        <FilterChip
          label={categoryName}
          onRemove={() =>
            setDraft((prev) => ({ ...prev, categoryId: ALL_CATEGORIES }))
          }
        />
      )}
      {isPartnersService &&
        draft.tagIds.map((tagId) => {
          const categoryLabel = categoryById.get(String(tagId));
          return categoryLabel ? (
            <FilterChip
              key={tagId}
              label={categoryLabel}
              onRemove={() => removePartnerTag(tagId)}
            />
          ) : null;
        })}
      {activePrice && (
        <FilterChip
          label={formatPriceRange(draft.price, priceMin, priceMax)}
          onRemove={() => setDraft((prev) => ({ ...prev, price: ANY_PRICE }))}
        />
      )}
      {draft.responsibleIds.map((id) => {
        const opt = staffAndTeamOptions.find((o) => o.value === id);
        return opt ? (
          <FilterChip
            key={id}
            label={opt.label}
            onRemove={() =>
              setDraft({
                ...draft,
                responsibleIds: draft.responsibleIds.filter((x) => x !== id),
              })
            }
          />
        ) : null;
      })}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type ServiceFilterPopUpProps = {
  filters: {
    providerType: string;
    price: string;
    category: string;
    partnerTagIds?: number[];
    status: string;
    responsibleIds: string[];
  } & Partial<HotelServicesFilters>;
  priceRange?: PriceRange;
  actions: {
    setPrice: (value: string) => void;
    setCategory: (value: string) => void;
    setPartnerTagIds: (value: number[]) => void;
    setStatus: (value: string) => void;
    setResponsibleIds: (value: string[]) => void;
    setCurrentPage: (page: number) => void;
    handleFilterChange: (
      setter: (value: string) => void,
      value: string,
    ) => void;
    resetFilters: (providerType?: string) => void;
  };
};

// ─── Main component ───────────────────────────────────────────────────────────

const ServiceFilterPopUp = ({
  filters,
  actions,
  priceRange,
}: ServiceFilterPopUpProps) => {
  const { hotelId } = useHotel();
  const isPartnersService =
    filters.providerType ===
    HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE;
  const isGuestService =
    filters.providerType === HotelServiceProviderType.GUEST_SERVICE;
  const { data: categoriesData } = useServiceCategories(
    { hotelId },
    { enabled: !isPartnersService },
  );
  const { data: partnerFilterOptions } = usePartnerServiceFilterOptions(
    hotelId,
    isPartnersService,
  );
  const { data: staffData } = useStaff(
    { hotelId: hotelId || "" },
    { enabled: isGuestService },
  );
  const { data: teamsData } = useTeams(
    {
      isActive: true,
      perPage: 100,
      sortBy: null,
      sortOrder: null,
    },
    { enabled: isGuestService },
  );
  const effectivePriceRange = isPartnersService
    ? partnerFilterOptions?.priceRange
    : priceRange;
  const priceMin = effectivePriceRange?.min ?? PRICE_MIN;
  const priceMax = effectivePriceRange?.max ?? PRICE_MAX;

  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<ServiceFilterDraft>(() =>
    draftFromFilters(filters),
  );
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [panelSearch, setPanelSearch] = React.useState("");

  const serviceCategories = (categoriesData?.items ?? []) as {
    id: string;
    name: string;
  }[];
  const partnerCategories = (partnerFilterOptions?.categories ?? []).map(
    (category) => ({
      id: String(category.tagId),
      name: category.name,
    }),
  );
  const categories = isPartnersService ? partnerCategories : serviceCategories;

  const staffOptions = (staffData?.users ?? [])
    .filter((u) => !!u.staff?.id)
    .map((u) => ({
      value: `staff:${u.staff!.id}`,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "",
      initials: getInitials(u.firstName, u.lastName),
    }));

  const teamOptions = (teamsData?.data ?? []).map((team) => ({
    value: `team:${team.id}`,
    label: `Team: ${team.name}`,
  }));

  const staffAndTeamOptions = [
    ...teamOptions.map((t) => ({
      ...t,
      initials: t.label.slice(0, 2).toUpperCase(),
    })),
    ...staffOptions,
  ];

  const activeCount = countActiveFilters(
    open ? draft : draftFromFilters(filters),
    isPartnersService,
  );

  const handleOpen = (next: boolean) => {
    setOpen(next);
    if (next) {
      setDraft(draftFromFilters(filters));
      setExpanded({});
      setPanelSearch("");
    }
  };

  const toggle = (section: string) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleApply = () => {
    const statusVal =
      draft.statuses.length === 0 || draft.statuses.length === 2
        ? ALL_STATUES
        : draft.statuses[0];
    actions.handleFilterChange(actions.setStatus, statusVal);
    if (isPartnersService) {
      actions.handleFilterChange(actions.setCategory, ALL_CATEGORIES);
      actions.setPartnerTagIds(draft.tagIds);
    } else {
      actions.handleFilterChange(actions.setCategory, draft.categoryId);
      actions.setPartnerTagIds([]);
    }
    actions.handleFilterChange(actions.setPrice, draft.price);
    actions.setResponsibleIds(draft.responsibleIds);
    actions.setCurrentPage(1);
    setOpen(false);
  };

  const handleReset = () => {
    actions.resetFilters(filters.providerType);
    setDraft(
      draftFromFilters({
        providerType: filters.providerType,
        price: ANY_PRICE,
        category: ALL_CATEGORIES,
        partnerTagIds: [],
        tagIds: [],
        status: ALL_STATUES,
        responsibleIds: [],
      }),
    );
  };

  const q = panelSearch.trim().toLowerCase();
  const showStatus =
    matchSearch("Status", q) ||
    matchSearch("Active", q) ||
    matchSearch("Inactive", q);
  const showCategory =
    matchSearch("Category", q) ||
    categories.some((c) => matchSearch(c.name, q));
  const showPrice = matchSearch("Price", q);
  const showManager =
    isGuestService &&
    (matchSearch("Manager", q) ||
      matchSearch("Responsible", q) ||
      staffAndTeamOptions.some((o) => matchSearch(o.label, q)));

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter className="size-4" />
          Filters{activeCount > 0 ? ` (${activeCount})` : ""}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base leading-normal font-semibold text-[#171717]">
              {activeCount > 0 ? `Filters (${activeCount})` : "Filters"}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label="Close filters"
            >
              <X className="size-5" />
            </Button>
          </div>
          <ActiveChips
            draft={draft}
            setDraft={setDraft}
            categories={categories}
            isPartnersService={isPartnersService}
            staffAndTeamOptions={staffAndTeamOptions}
            priceMin={priceMin}
            priceMax={priceMax}
          />
        </div>

        {/* Panel search */}
        <div className="relative shrink-0">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder="Type a command or search"
            value={panelSearch}
            onChange={(e) => setPanelSearch(e.target.value)}
            className="h-9 border-[#E5E5E5] pr-9 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
          />
          {panelSearch && (
            <Button
              type="button"
              variant="span"
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
              onClick={() => setPanelSearch("")}
              aria-label="Clear search"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* Sections */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
          {showStatus && (
            <StatusSection
              draft={draft}
              setDraft={setDraft}
              expanded={!!expanded.status}
              onToggle={() => toggle("status")}
            />
          )}
          {showPrice && (
            <PriceSection
              draft={draft}
              setDraft={setDraft}
              min={priceMin}
              max={priceMax}
              expanded={!!expanded.price}
              onToggle={() => toggle("price")}
            />
          )}
          {showCategory && (
            <CategorySection
              draft={draft}
              setDraft={setDraft}
              categories={categories}
              multiSelect={isPartnersService}
              expanded={!!expanded.category}
              onToggle={() => toggle("category")}
            />
          )}
          {showManager && (
            <ResponsibleSection
              draft={draft}
              setDraft={setDraft}
              staffOptions={staffOptions}
              teamOptions={teamOptions}
              expanded={!!expanded.manager}
              onToggle={() => toggle("manager")}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={handleApply}
          >
            Apply filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ServiceFilterPopUp;
